/**
 * TraceTable — single source of truth for every trace drill-down table in
 * the dashboard.
 *
 * Consumed by:
 *   - TimeSavedBreakdownModal (Overview → Time saved chart → bucket click)
 *   - TraceDrillDownModal     (Overview / Quality / Operations → chart click)
 *
 * Why one component:
 *   The user reported on 2026-04-24 that the two modals' mobile views looked
 *   visibly different — because each modal had its own <Table> with its own
 *   colgroup of hard-coded pixel widths and no responsive layout. A shared
 *   Q/A cell was not enough; the whole table needs to come from one place.
 *
 * Layouts:
 *   - Desktop (`md:` and up): a `<Table table-fixed>` with a colgroup built
 *     dynamically from the `columns` prop. Column widths live in COL_WIDTH
 *     so both modals pick identical values.
 *   - Mobile (<md):          a stacked card list, one article per row.
 *     Primary metrics (time, kind, rating, latency, cost, min-saved) live
 *     in the card header; the user identity, Q/A and FB occupy the body.
 *
 * Columns prop:
 *   Callers pass a subset/ordering of `TraceColumnKey`. Missing columns are
 *   not rendered on either layout. This keeps the Time-saved modal (which
 *   carries `minSaved`) and the drill-down modal (which does not) driving
 *   the same component with the same markup.
 *
 * Feedback:
 *   Free-text feedback is its own column (`feedback`), not a line inside
 *   QueryAnswerCell — matching the 2026-04-24 user ask to surface it as a
 *   first-class, sortable-adjacent field.
 */

import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { FeedbackCell } from '@/components/FeedbackCell';
import { formatLatency, formatRating, formatTraceCost } from '@/lib/format';
import { QueryAnswerCell } from '@/components/QueryAnswerCell';
import { UserNameCell } from '@/components/UserNameCell';
import { TraceDetailPanel } from '@/components/trace/TraceDetailPanel';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeedbackTraceDetail } from '@/hooks/use-feedback-trace-detail';
import { formatDateTime } from '@/lib/format-date';
import { isBriefTraceName, kindLabel } from '@/lib/trace-labels';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Superset trace row accepted by TraceTable. Both `TimeSavedBreakdownItem`
 * and `TraceListRow` map into this at the callsite — harmonising around
 * `userId` / `cost` (snake off `username` / `totalCost` in the Time-saved
 * hook). Optional fields render only when their column is enabled.
 */
export interface TraceTableRow {
  traceId: string;
  timestamp: string;
  userId: string;
  realName: string | null;
  name: string;
  complexity: string | null;
  rating: number | null;
  timeSavedMinutes?: number | null;
  latencyMs: number | null;
  cost: number | null;
  input: string | null;
  output: string | null;
  error: string | null;
  textFeedback: string | null;
  safetyRefused?: boolean;
  hasError?: boolean;
  /** Display-only RM/Non-RM badge for the optional `role` column. */
  isRm?: boolean;
  /** What came of the row, one short line — the optional `outcome` column (the Contacts drill-down:
   *  «Imported · 5 sent · 4 created · 1 refused»). */
  outcome?: string | null;
}

export type TraceColumnKey =
  | 'time'
  | 'user'
  | 'role'
  | 'kind'
  | 'complexity'
  | 'rating'
  | 'minSaved'
  | 'latency'
  | 'cost'
  | 'queryAnswer'
  | 'feedback'
  | 'outcome';

export type TraceSortKey =
  | 'timestamp'
  | 'username'
  | 'isRm'
  | 'name'
  | 'complexity'
  | 'rating'
  | 'timeSavedMinutes'
  | 'latencyMs'
  | 'cost';

export interface TraceTableSort {
  key: TraceSortKey;
  dir: 'asc' | 'desc';
}

export interface TraceTableProps {
  rows: TraceTableRow[];
  columns: TraceColumnKey[];
  sort: TraceTableSort;
  onSort: (key: TraceSortKey) => void;
  /** Forwarded to UserNameCell so the `/user/:username` link stays scoped to the drill-down window. */
  from?: string;
  to?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  emptyMessage?: string;
  /** Optional footer node (trace count, total minutes saved, etc.). */
  footer?: React.ReactNode;
  testIdPrefix: string;
  /**
   * Phase 8 — when true, each row gets a chevron toggle and renders an
   * inline detail panel below itself when expanded. Off by default — the
   * two existing modal call sites (`TraceDrillDownModal`,
   * `TimeSavedBreakdownModal`) omit this prop and remain unchanged
   * visually.
   */
  expandable?: boolean;
  /**
   * Phase 8 — set of currently-expanded traceIds. Caller owns the source
   * of truth (URL-mirrored for the Activity tab). Required when
   * `expandable` is true.
   */
  expandedIds?: ReadonlySet<string>;
  /**
   * Phase 8 — toggle handler. The component does NOT manage expand state
   * internally — caller persists to URL or local state per surface.
   */
  onToggleExpanded?: (traceId: string) => void;
  /**
   * Phase 8 — optional ReactNode rendered above the table head (and
   * above the mobile card list). Used by the Activity tab to render its
   * per-tab toolbar (rating select, search input, errorsOnly toggle,
   * hasFeedback toggle). Modals omit it.
   */
  toolbarSlot?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// LazyTraceDetail — co-located helper, mirror of FeedbackTable.tsx:219-291
// ---------------------------------------------------------------------------

/**
 * Owns the on-expand `/feedback/trace-detail` fetch for a single trace and
 * adapts the response into the shape `<TraceDetailPanel>` expects. Keyed
 * on `traceId` with `staleTime: 5 min` inside `useFeedbackTraceDetail`, so
 * collapsing + re-expanding a row never refetches.
 *
 * Mirror of `FeedbackTable.tsx:RowExpandPanel` — kept verbatim so any fix
 * applied to one surface ports cleanly to the other.
 */
interface LazyTraceDetailProps {
  traceId: string;
  feedback: { rating: number | null; text: string | null } | null;
  /** Server-side preview snippets shown while the network is in flight. */
  inputPreview?: string | null;
  outputPreview?: string | null;
  /** Trace metadata available from the row — fills the header before
   *  the network resolves. */
  timestamp?: string | null;
  durationMs?: number | null;
  cost?: number | null;
  /** True for delivered-brief traces (`isBriefTraceName(row.name)`) — renders
   *  the full brief instead of the "sent N messages" status string. */
  isBrief?: boolean;
}

function LazyTraceDetail({
  traceId,
  feedback,
  inputPreview,
  outputPreview,
  timestamp,
  durationMs,
  cost,
  isBrief,
}: LazyTraceDetailProps): React.ReactElement {
  const detail = useFeedbackTraceDetail(traceId, true);

  if (detail.isLoading) {
    return (
      <div className="space-y-2" data-testid="trace-row-expand-loading">
        <div className="h-3 w-24 rounded bg-muted animate-pulse" />
        <div className="h-3 w-full rounded bg-muted animate-pulse" />
        <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
        <div className="h-3 w-20 rounded bg-muted animate-pulse mt-2" />
        <div className="h-3 w-full rounded bg-muted animate-pulse" />
      </div>
    );
  }

  if (detail.isError) {
    return (
      <div
        className="space-y-2 text-sm"
        data-testid="trace-row-expand-fetch-error"
      >
        <div className="font-medium text-red-600">
          Failed to load trace detail.
        </div>
        <div className="text-xs text-muted-foreground">
          The Langfuse fetch did not return — the trace may have rotated out
          of retention, or the upstream is unreachable.
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void detail.refetch()}
          data-testid="trace-row-expand-retry"
        >
          Retry
        </Button>
      </div>
    );
  }

  const data = detail.data;
  return (
    <TraceDetailPanel
      variant="inline"
      detail={{
        traceId,
        timestamp: timestamp ?? null,
        durationMs: durationMs ?? null,
        cost: cost ?? null,
        input: data?.input ?? inputPreview ?? null,
        output: data?.output ?? outputPreview ?? null,
        // The blocks the CODE drew beside the answer — the connect card. No preview
        // fallback: the mirror row carries no such snippet, so the section simply
        // appears when the on-expand fetch lands.
        codeDrawn: data?.codeDrawn ?? null,
        error: data?.error ?? null,
      }}
      feedback={feedback}
      isBrief={isBrief}
    />
  );
}

// ---------------------------------------------------------------------------
// Column config — single source of truth for labels, widths, alignment, sort
// ---------------------------------------------------------------------------

interface ColumnConfig {
  label: string;
  sortKey: TraceSortKey | null;
  align: 'left' | 'right';
  desktopWidth: string | null;
}

const COLUMN_CONFIG: Record<TraceColumnKey, ColumnConfig> = {
  time: { label: 'Time', sortKey: 'timestamp', align: 'left', desktopWidth: '112px' },
  user: { label: 'User', sortKey: 'username', align: 'left', desktopWidth: '160px' },
  role: { label: 'Role', sortKey: 'isRm', align: 'left', desktopWidth: '72px' },
  kind: { label: 'Kind', sortKey: 'name', align: 'left', desktopWidth: '140px' },
  complexity: { label: 'Complexity', sortKey: 'complexity', align: 'left', desktopWidth: '96px' },
  rating: { label: 'Rating', sortKey: 'rating', align: 'right', desktopWidth: '64px' },
  minSaved: { label: 'Min saved', sortKey: 'timeSavedMinutes', align: 'right', desktopWidth: '80px' },
  latency: { label: 'Latency', sortKey: 'latencyMs', align: 'right', desktopWidth: '76px' },
  cost: { label: 'Cost', sortKey: 'cost', align: 'right', desktopWidth: '72px' },
  queryAnswer: { label: 'Query / Answer', sortKey: null, align: 'left', desktopWidth: null },
  feedback: { label: 'Feedback', sortKey: null, align: 'left', desktopWidth: '220px' },
  outcome: { label: 'Outcome', sortKey: null, align: 'left', desktopWidth: '200px' },
};

// ---------------------------------------------------------------------------
// Formatters (local — keep TraceTable self-contained; callers don't need to
// re-implement these for consistency)
// ---------------------------------------------------------------------------

function formatTime(iso: string): string {
  return formatDateTime(iso);
}


function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  if (minutes === 0) return '0';
  const m = Math.round(minutes * 10) / 10;
  return m < 10 ? m.toFixed(1) : Math.round(m).toString();
}


// ---------------------------------------------------------------------------
// Sort header button
// ---------------------------------------------------------------------------

function SortButton({
  column,
  sort,
  onSort,
  testId,
}: {
  column: ColumnConfig;
  sort: TraceTableSort;
  onSort: (k: TraceSortKey) => void;
  testId?: string;
}): React.ReactElement {
  if (!column.sortKey) {
    return (
      <span
        className={`flex w-full items-center font-semibold text-muted-foreground ${
          column.align === 'right' ? 'justify-end' : 'justify-start'
        }`}
      >
        {column.label}
      </span>
    );
  }
  const active = sort.key === column.sortKey;
  const arrow = active ? (sort.dir === 'asc' ? '↑' : '↓') : '';
  return (
    <button
      type="button"
      onClick={() => onSort(column.sortKey as TraceSortKey)}
      className={`flex w-full items-center gap-1 font-semibold whitespace-nowrap hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm ${
        active ? 'text-foreground' : 'text-muted-foreground'
      } ${column.align === 'right' ? 'justify-end' : 'justify-start'}`}
      data-testid={testId}
    >
      <span>{column.label}</span>
      <span
        aria-hidden="true"
        className="inline-block w-3 text-center tabular-nums text-[11px] text-foreground"
      >
        {arrow}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Desktop cell renderers — one `renderDesktopCell` per column
// ---------------------------------------------------------------------------

function renderDesktopCell(
  col: TraceColumnKey,
  row: TraceTableRow,
  ctx: { from?: string; to?: string; testIdPrefix: string },
): React.ReactNode {
  switch (col) {
    case 'time':
      return (
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">
          {formatTime(row.timestamp)}
        </span>
      );
    case 'user':
      return (
        <UserNameCell
          username={row.userId}
          realName={row.realName}
          from={ctx.from}
          to={ctx.to}
          dataTestId={`${ctx.testIdPrefix}-user`}
        />
      );
    case 'role':
      // Solid-green pill for RMs mirrors the Quality-tab RM badge; Non-RM
      // stays muted so at-a-glance RM scanning is the primary affordance.
      return row.isRm ? (
        <span
          data-testid={`${ctx.testIdPrefix}-role`}
          data-role="rm"
          className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400"
        >
          RM
        </span>
      ) : (
        <span
          data-testid={`${ctx.testIdPrefix}-role`}
          data-role="non-rm"
          className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Non-RM
        </span>
      );
    case 'kind':
      return (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <span className="block truncate whitespace-nowrap text-muted-foreground cursor-default">
              {kindLabel(row.name)}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="start"
            sideOffset={6}
            collisionPadding={16}
            // `text-left` overrides the default centered-text on TooltipContent;
            // a stacked label + raw event-name reads as a tiny key/value card,
            // not a centred caption.
            className="max-w-[min(50vw,40rem)] max-h-[70vh] overflow-y-auto whitespace-pre-wrap break-words text-left text-xs leading-relaxed"
          >
            <div className="space-y-0.5">
              <div className="font-medium">{kindLabel(row.name)}</div>
              <div className="text-muted-foreground font-mono text-[11px]">
                {row.name}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    case 'complexity':
      return (
        <span className="block truncate whitespace-nowrap text-muted-foreground">
          {row.complexity ?? '—'}
        </span>
      );
    case 'rating':
      return (
        <span className="tabular-nums">{formatRating(row.rating)}</span>
      );
    case 'minSaved':
      return (
        <span className="tabular-nums font-semibold">
          {formatMinutes(row.timeSavedMinutes)}
        </span>
      );
    case 'latency':
      return (
        <span className="tabular-nums text-muted-foreground">
          {formatLatency(row.latencyMs)}
        </span>
      );
    case 'cost':
      return (
        <span className="tabular-nums text-muted-foreground">
          {formatTraceCost(row.cost)}
        </span>
      );
    case 'queryAnswer':
      return (
        <QueryAnswerCell
          input={row.input}
          output={row.output}
          error={row.error ?? (row.hasError ? 'error' : null)}
          kind={row.name}
          safetyRefused={row.safetyRefused}
        />
      );
    case 'feedback':
      return <FeedbackCell text={row.textFeedback} placeholder />;
    case 'outcome':
      return (
        <span data-testid={`${ctx.testIdPrefix}-outcome`} className="text-xs">
          {row.outcome ?? '—'}
        </span>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Mobile card — renders a subset of columns with a friendly stacked layout
// ---------------------------------------------------------------------------

function MobileCard({
  row,
  columns,
  ctx,
  expandable = false,
  isExpanded = false,
  onToggle,
}: {
  row: TraceTableRow;
  columns: TraceColumnKey[];
  ctx: { from?: string; to?: string; testIdPrefix: string };
  expandable?: boolean;
  isExpanded?: boolean;
  onToggle?: (traceId: string) => void;
}): React.ReactElement {
  const has = (k: TraceColumnKey): boolean => columns.includes(k);

  const handleClick = (e: React.MouseEvent): void => {
    if (!expandable) return;
    // Ignore clicks on nested links / buttons so the user-profile / sort
    // headers / action chips still work without the parent swallowing them.
    const target = e.target as HTMLElement | null;
    if (target && target.closest('a, button')) return;
    onToggle?.(row.traceId);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
  ): void => {
    if (!expandable) return;
    const target = e.target as HTMLElement | null;
    if (target && target !== e.currentTarget && target.closest('a, button')) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onToggle?.(row.traceId);
    }
  };

  const headerRightItems: Array<{ key: string; node: React.ReactNode }> = [];
  if (has('rating') && row.rating != null) {
    headerRightItems.push({
      key: 'rating',
      node: (
        <span className="tabular-nums font-medium text-foreground">
          {formatRating(row.rating)}
        </span>
      ),
    });
  }
  if (has('minSaved') && row.timeSavedMinutes != null) {
    headerRightItems.push({
      key: 'min',
      node: (
        <span className="tabular-nums font-semibold text-foreground">
          +{formatMinutes(row.timeSavedMinutes)}m
        </span>
      ),
    });
  }
  if (has('latency')) {
    headerRightItems.push({
      key: 'lat',
      node: (
        <span className="tabular-nums text-muted-foreground">
          {formatLatency(row.latencyMs)}
        </span>
      ),
    });
  }
  if (has('cost')) {
    headerRightItems.push({
      key: 'cost',
      node: (
        <span className="tabular-nums text-muted-foreground">
          {formatTraceCost(row.cost)}
        </span>
      ),
    });
  }

  return (
    <article
      data-testid={`${ctx.testIdPrefix}-card`}
      data-trace-id={row.traceId}
      data-expanded={expandable ? (isExpanded ? 'true' : 'false') : undefined}
      role={expandable ? 'button' : undefined}
      tabIndex={expandable ? 0 : undefined}
      aria-expanded={expandable ? isExpanded : undefined}
      onClick={expandable ? handleClick : undefined}
      onKeyDown={expandable ? handleKeyDown : undefined}
      className={cn(
        'rounded-md border border-border bg-background p-3 text-xs space-y-2',
        expandable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isExpanded && 'bg-muted/40',
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-0.5">
          {has('time') && (
            <time
              dateTime={row.timestamp}
              className="block tabular-nums text-muted-foreground"
            >
              {formatTime(row.timestamp)}
            </time>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-muted-foreground">
            {has('kind') && (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="truncate text-foreground cursor-default">
                    {kindLabel(row.name)}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  sideOffset={6}
                  collisionPadding={16}
                  // See desktop-cell tooltip above — `text-left` defeats the
                  // TooltipContent centered default so the stacked
                  // label/raw-name reads as a key/value, not a caption.
                  className="max-w-[min(80vw,28rem)] max-h-[70vh] overflow-y-auto whitespace-pre-wrap break-words text-left text-xs leading-relaxed"
                >
                  <div className="space-y-0.5">
                    <div className="font-medium">{kindLabel(row.name)}</div>
                    <div className="text-muted-foreground font-mono text-[11px]">
                      {row.name}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            {has('complexity') && row.complexity && (
              <span>· {row.complexity}</span>
            )}
          </div>
        </div>
        {(headerRightItems.length > 0 || expandable) && (
          <div className="flex flex-col items-end gap-0.5 whitespace-nowrap">
            {headerRightItems.map((item) => (
              <span key={item.key}>{item.node}</span>
            ))}
            {expandable && (
              <span
                data-testid={`${ctx.testIdPrefix}-chevron`}
                aria-hidden="true"
                className="text-muted-foreground"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
            )}
          </div>
        )}
      </header>

      {has('user') && (
        <div className="flex items-center gap-2">
          <UserNameCell
            username={row.userId}
            realName={row.realName}
            from={ctx.from}
            to={ctx.to}
            dataTestId={`${ctx.testIdPrefix}-user`}
          />
          {has('role') && (
            row.isRm ? (
              <span
                data-testid={`${ctx.testIdPrefix}-role`}
                data-role="rm"
                className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400"
              >
                RM
              </span>
            ) : (
              <span
                data-testid={`${ctx.testIdPrefix}-role`}
                data-role="non-rm"
                className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Non-RM
              </span>
            )
          )}
        </div>
      )}

      {has('queryAnswer') && (
        <QueryAnswerCell
          input={row.input}
          output={row.output}
          error={row.error ?? (row.hasError ? 'error' : null)}
          kind={row.name}
          safetyRefused={row.safetyRefused}
        />
      )}

      {has('outcome') && row.outcome && (
        <div data-testid={`${ctx.testIdPrefix}-outcome`} className="text-xs">
          {row.outcome}
        </div>
      )}

      {has('feedback') && row.textFeedback && (
        <FeedbackCell text={row.textFeedback} />
      )}

      {expandable && isExpanded && (
        <div
          data-testid={`${ctx.testIdPrefix}-row-expanded`}
          data-trace-id={row.traceId}
          className="border-t pt-2"
        >
          <LazyTraceDetail
            traceId={row.traceId}
            feedback={
              row.rating != null || row.textFeedback
                ? { rating: row.rating, text: row.textFeedback }
                : null
            }
            inputPreview={row.input}
            outputPreview={row.output}
            timestamp={row.timestamp}
            durationMs={row.latencyMs}
            cost={row.cost}
            isBrief={isBriefTraceName(row.name)}
          />
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** Cold-tab fallback only: rows to show when this table has never had any. */
const SKELETON_ROWS = 6;

export function TraceTable({
  rows,
  columns,
  sort,
  onSort,
  from,
  to,
  isLoading = false,
  isError = false,
  onRetry,
  emptyMessage = 'No traces match this slice.',
  footer,
  testIdPrefix,
  expandable = false,
  expandedIds,
  onToggleExpanded,
  toolbarSlot,
}: TraceTableProps): React.ReactElement {
  const ctx = { from, to, testIdPrefix };

  // dev-only guard — Pitfall 3
  if (
    process.env.NODE_ENV !== 'production' &&
    expandable &&
    (!expandedIds || !onToggleExpanded)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[TraceTable] expandable=true requires both `expandedIds` and `onToggleExpanded` props.',
    );
  }

  const handleRowClick = (
    traceId: string,
    e: React.MouseEvent<HTMLTableRowElement>,
  ): void => {
    if (!expandable) return;
    const target = e.target as HTMLElement | null;
    if (target && target.closest('a, button')) return;
    onToggleExpanded?.(traceId);
  };

  const handleRowKeyDown = (
    traceId: string,
    e: React.KeyboardEvent<HTMLTableRowElement>,
  ): void => {
    if (!expandable) return;
    const target = e.target as HTMLElement | null;
    if (target && target !== e.currentTarget && target.closest('a, button')) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onToggleExpanded?.(traceId);
    }
  };

  /* Sized to what it replaces: the activity feed renders a full page of 50,
     and a fixed six collapsed it by a screen on every filter change. Recorded
     after the render that showed the rows; the fallback is the cold tab. */
  const lastRowCount = React.useRef(SKELETON_ROWS);
  React.useEffect(() => {
    if (rows.length > 0) lastRowCount.current = rows.length;
  }, [rows.length]);
  const skeletonRows = lastRowCount.current;

  if (isLoading) {
    // The toolbar stays, for the same reason the empty-rows branch below keeps
    // it — and since the recount rule (hooks/use-filtered-query.ts) this branch
    // is no longer reached only on a first mount: it is reached on every change
    // of the filters, INCLUDING the ones this toolbar makes. The search box
    // holds its text in local state and emits on a debounce, so unmounting it
    // here threw away the focus and any character typed while the request was
    // in flight.
    const skeleton = (
      <div data-testid={`${testIdPrefix}-loading`} className="space-y-2 p-1">
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <div
            key={i}
            className="h-10 rounded-md border border-border bg-muted/40 animate-pulse"
          />
        ))}
      </div>
    );
    if (!toolbarSlot) return skeleton;
    return (
      <TooltipProvider delayDuration={200}>
        <div
          data-testid={`${testIdPrefix}-toolbar`}
          className="sticky top-0 z-20 bg-background border-b border-border"
        >
          {toolbarSlot}
        </div>
        {skeleton}
      </TooltipProvider>
    );
  }

  if (isError) {
    return (
      <div
        data-testid={`${testIdPrefix}-error`}
        className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-background p-3 text-sm text-destructive"
      >
        <span>Failed to load traces for this slice.</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (rows.length === 0) {
    // Phase 8 Plan 05 — when a toolbar is configured (Activity tab), keep
    // it visible above the empty-state so users can clear/adjust filters
    // that produced the empty result. Without this, a filter that returns
    // zero rows hides the toolbar and the user is stuck on a no-data
    // screen with no UI to back out (ACT-04 reachability).
    if (toolbarSlot) {
      return (
        <TooltipProvider delayDuration={200}>
          <div
            data-testid={`${testIdPrefix}-toolbar`}
            className="sticky top-0 z-20 bg-background border-b border-border"
          >
            {toolbarSlot}
          </div>
          <div
            data-testid={`${testIdPrefix}-empty`}
            className="rounded-md border border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground"
          >
            {emptyMessage}
          </div>
        </TooltipProvider>
      );
    }
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        className="rounded-md border border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground"
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      {toolbarSlot && (
        <div
          data-testid={`${testIdPrefix}-toolbar`}
          className="sticky top-0 z-20 bg-background border-b border-border"
        >
          {toolbarSlot}
        </div>
      )}

      {/* Mobile (<md): stacked cards. */}
      <div
        data-testid={`${testIdPrefix}-cards`}
        className="md:hidden flex flex-col gap-2"
      >
        {rows.map((row) => (
          <MobileCard
            key={row.traceId}
            row={row}
            columns={columns}
            ctx={ctx}
            expandable={expandable}
            isExpanded={expandable && (expandedIds?.has(row.traceId) ?? false)}
            onToggle={onToggleExpanded}
          />
        ))}
      </div>

      {/* Desktop (md+): fixed-layout table. */}
      <div className="hidden md:block">
        <Table
          data-testid={`${testIdPrefix}-table`}
          className="text-xs table-fixed w-full"
        >
          <colgroup>
            {expandable && <col style={{ width: '28px' }} />}
            {columns.map((col) => (
              <col
                key={col}
                style={
                  COLUMN_CONFIG[col].desktopWidth
                    ? { width: COLUMN_CONFIG[col].desktopWidth as string }
                    : undefined
                }
              />
            ))}
          </colgroup>
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              {expandable && (
                <TableHead
                  className="h-9 px-2 w-7"
                  aria-label="Toggle"
                />
              )}
              {columns.map((col) => (
                <TableHead key={col} className="h-9 px-2">
                  <SortButton
                    column={COLUMN_CONFIG[col]}
                    sort={sort}
                    onSort={onSort}
                    testId={
                      COLUMN_CONFIG[col].sortKey
                        ? `${testIdPrefix}-sort-${COLUMN_CONFIG[col].sortKey}`
                        : undefined
                    }
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const isExpanded =
                expandable && (expandedIds?.has(row.traceId) ?? false);
              return (
                <React.Fragment key={row.traceId}>
                  <TableRow
                    data-testid={`${testIdPrefix}-row`}
                    data-trace-id={row.traceId}
                    data-expanded={
                      expandable ? (isExpanded ? 'true' : 'false') : undefined
                    }
                    role={expandable ? 'button' : undefined}
                    tabIndex={expandable ? 0 : undefined}
                    aria-expanded={expandable ? isExpanded : undefined}
                    onClick={
                      expandable
                        ? (e) => handleRowClick(row.traceId, e)
                        : undefined
                    }
                    onKeyDown={
                      expandable
                        ? (e) => handleRowKeyDown(row.traceId, e)
                        : undefined
                    }
                    className={cn(
                      expandable &&
                        'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isExpanded && 'bg-muted/40',
                    )}
                  >
                    {expandable && (
                      <TableCell className="w-6 px-2 align-top">
                        <span
                          data-testid={`${testIdPrefix}-chevron`}
                          aria-hidden="true"
                          className="text-muted-foreground"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </span>
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell
                        key={col}
                        className={`px-2 align-top ${
                          COLUMN_CONFIG[col].align === 'right'
                            ? 'text-right'
                            : ''
                        }`}
                      >
                        {renderDesktopCell(col, row, ctx)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {isExpanded && (
                    <TableRow
                      data-testid={`${testIdPrefix}-row-expanded`}
                      data-trace-id={row.traceId}
                      className="bg-muted/20"
                    >
                      <TableCell
                        colSpan={columns.length + 1}
                        className="px-3 py-3"
                      >
                        <LazyTraceDetail
                          traceId={row.traceId}
                          feedback={
                            row.rating != null || row.textFeedback
                              ? {
                                  rating: row.rating,
                                  text: row.textFeedback,
                                }
                              : null
                          }
                          inputPreview={row.input}
                          outputPreview={row.output}
                          timestamp={row.timestamp}
                          durationMs={row.latencyMs}
                          cost={row.cost}
                          isBrief={isBriefTraceName(row.name)}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {footer && (
        <div
          data-testid={`${testIdPrefix}-footer`}
          className="border-t border-border pt-2 text-xs tabular-nums text-muted-foreground mt-2"
        >
          {footer}
        </div>
      )}
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Shared comparator — both modals sort client-side on the same key contract
// ---------------------------------------------------------------------------

export function compareRows(
  a: TraceTableRow,
  b: TraceTableRow,
  sort: TraceTableSort,
): number {
  const mul = sort.dir === 'asc' ? 1 : -1;
  const COMPLEXITY_ORDER: Record<string, number> = {
    trivial: 0,
    simple: 1,
    medium: 2,
    complex: 3,
    expert: 4,
  };
  let av: unknown;
  let bv: unknown;
  switch (sort.key) {
    case 'timestamp':
      av = a.timestamp;
      bv = b.timestamp;
      break;
    case 'username':
      av = (a.realName ?? a.userId ?? '').toLowerCase();
      bv = (b.realName ?? b.userId ?? '').toLowerCase();
      break;
    case 'isRm':
      av = a.isRm ? 1 : 0;
      bv = b.isRm ? 1 : 0;
      break;
    case 'name':
      av = a.name;
      bv = b.name;
      break;
    case 'complexity':
      av = a.complexity ? COMPLEXITY_ORDER[a.complexity] : null;
      bv = b.complexity ? COMPLEXITY_ORDER[b.complexity] : null;
      break;
    case 'rating':
      av = a.rating;
      bv = b.rating;
      break;
    case 'timeSavedMinutes':
      av = a.timeSavedMinutes;
      bv = b.timeSavedMinutes;
      break;
    case 'latencyMs':
      av = a.latencyMs;
      bv = b.latencyMs;
      break;
    case 'cost':
      av = a.cost;
      bv = b.cost;
      break;
  }
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
  return String(av).localeCompare(String(bv)) * mul;
}

export function sortRows(
  rows: TraceTableRow[],
  sort: TraceTableSort,
): TraceTableRow[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    const cmp = compareRows(a, b, sort);
    if (cmp !== 0) return cmp;
    return a.traceId.localeCompare(b.traceId);
  });
  return copy;
}
